import React from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { brand } from '../brand';

/**
 * Free and Premium for a job seeker as one table: Feature, Free, Premium.
 *
 * Each feature is one line with a green tick where the plan has it and a red cross where it does not, grouped by the
 * parts of the Career Wheel (plus Interactions, the ways of reaching RoRi). A limit sits in the cell, beside the tick.
 *
 * Every claim is something the application does today, in the words its docs use.
 *
 * Every number comes from `limits`, which the server publishes from the settings in force and from the constants its
 * routes enforce (services/planLimits.js). No allowance or limit is written in this file. A number that is not there
 * is left off the line; a feature that is switched off is left out of the table (`limits.matches === null` means job
 * matching is off, while no `limits` at all means they could not be read, and then the features show with no figures).
 */

/** Read by a screen reader, not seen: the words behind a tick or a cross. */
const visuallyHidden = {
  border: 0, clip: 'rect(0 0 0 0)', height: '1px', margin: '-1px', overflow: 'hidden',
  padding: 0, position: 'absolute', whiteSpace: 'nowrap', width: '1px',
};

const toneOf = (theme, name) => (theme.palette[name] || theme.palette.primary).main;

const whole = (value) => Number.isFinite(value);

/** A piece of a limit sentence: the number it needs and how to say it. */
const part = (value, text) => ({ value, text });

/** The sentence from its pieces, or nothing at all if any piece did not arrive, so a figure is never half-printed. */
const note = (parts) => (parts.every((piece) => whole(piece.value)) ? parts.map((piece) => piece.text(piece.value)).join('') : null);

/** Present, with a limit if one can be said. */
const present = (parts) => {
  const text = note(parts);
  return text ? { note: text } : true;
};

/**
 * The groups. A feature is `{ key, name, free, premium }` where each plan is `true`, `false`, or `{ note }` (present,
 * with a limit).
 */
export const comparisonGroups = (limits) => {
  const known = Boolean(limits);
  // `null` for a group of limits is the server saying that feature is switched off.
  const off = (name) => known && limits[name] === null;
  const m = limits?.matches;
  // What a free candidate gets of matching, or null while an operator has the free tier off (and so nothing is promised).
  const free = m?.free || null;
  // A free allowance of none is that feature not offered, so it is not a tick with "none a month" beside it.
  const given = (value) => whole(value) && Boolean(value);
  const freeAllowance = (value, text) => (given(value) ? present([part(value, text)]) : false);
  const p = limits?.people;

  const groups = [
    {
      key: 'profile', tone: 'primary', icon: BadgeOutlinedIcon, title: 'Candidate profile',
      features: [
        { key: 'create-resume', name: `Create your resume in ${brand.name}`, free: true, premium: true },
        { key: 'upload-resume', name: 'Upload your resume', free: true, premium: true },
        { key: 'download-pdf', name: 'Download your resume as a PDF', free: true, premium: true },
        { key: 'publish-profile', name: 'Publish a profile page (optional)', free: true, premium: true },
      ],
    },
    {
      key: 'search', tone: 'success', icon: WorkOutlineIcon, title: 'Job search',
      features: [
        { key: 'browse-jobs', name: 'Browse open jobs', free: true, premium: true },
        { key: 'search-words', name: 'Search by words, in any order', free: true, premium: true },
        { key: 'best-match-sort', name: 'Sort jobs by best match', free: true, premium: true },
        { key: 'track-link', name: 'Track any job from a link', free: true, premium: true },
        { key: 'considering', name: 'Mark a job as one you are considering', free: true, premium: true },
      ],
    },
    {
      key: 'matches', tone: 'info', icon: AutoAwesomeIcon, title: 'Job match',
      // Job matching switched off means none of these exist, and the group is not shown.
      features: off('matches') ? [] : [
        {
          // Free for everybody today: "Top matches for you" on Browse, picked by rules with no model and no consent step.
          key: 'matches-list', name: 'Matches list of your strongest jobs',
          free: present([part(m?.topMatches, (v) => `up to ${v}`)]),
          premium: present([part(m?.shown, (v) => `up to ${v}`)]),
        },
        // The paid part of matching is these three rows: a score with its reasons, a match for a job from a link, and the role
        // fit radar. The same three are named on the Premium card and in the FAQ (`PREMIUM_MATCHING` in Home.js).
        // Jobs on our own board are scored free of charge when the free tier is on; the allowance on the next row is for a job from a link.
        { key: 'match-score', name: 'Match score with reasons, and the skills you have or lack', free: Boolean(free), premium: true },
        {
          key: 'link-match', name: 'Link match: score a job from a link or pasted text',
          free: free ? freeAllowance(free.linkMatchesPerMonth, (v) => `${v} a month`) : false,
          premium: present([part(m?.linkMatchesPerMonth, (v) => `up to ${v} a month`)]),
        },
        {
          key: 'match-runs', name: 'Match runs',
          free: free ? freeAllowance(free.runsPerDay, (v) => `${v} a day`) : false,
          premium: present([part(m?.runsPerDay, (v) => `up to ${v} a day`)]),
        },
        {
          key: 'fit-radar', name: 'Role fit radar',
          free: free ? freeAllowance(free.radarsPerMonth, (v) => `${v} a month`) : false,
          premium: present([part(m?.radarsPerMonth, (v) => `up to ${v} a month`)]),
        },
        // Strengthening a resume comes from a match, so it sits here and goes with the group when matching is off.
        { key: 'strengthen-resume', name: 'Strengthen your resume for a job', free: false, premium: true },
      ],
    },
    {
      key: 'practice', tone: 'primary', icon: QuestionAnswerOutlinedIcon, title: 'Practice interviews',
      features: [
        {
          key: 'practice-interview', name: 'Practice interview for a job',
          // Free has a smaller allowance of its own (one a month to start with); none when an operator sets it to zero.
          free: freeAllowance(limits?.practice?.freePerMonth, (v) => `${v} a month`),
          premium: present([part(limits?.practice?.perMonth, (v) => `${v} a month`)]),
        },
        { key: 'written-feedback', name: 'Written feedback', free: given(limits?.practice?.freePerMonth), premium: true },
        { key: 'transcript-recording', name: 'Transcript and recording', free: given(limits?.practice?.freePerMonth), premium: true },
      ],
    },
    {
      key: 'network', tone: 'info', icon: ContactsOutlinedIcon, title: 'Network',
      features: [
        { key: 'network-list', name: 'Keep your network list', free: true, premium: true },
        { key: 'follow-ups', name: 'Follow-up reminders', free: true, premium: true },
        { key: 'import-csv', name: 'Import contacts from a CSV file', free: true, premium: true },
        ...(off('people') ? [] : [{
          key: 'find-people', name: 'Find people at a company', free: false,
          premium: present([part(p?.perJob, (v) => `${v} per job, `), part(p?.perMonth, (v) => `${v} a month`)]),
        }]),
      ],
    },
    {
      key: 'events', tone: 'info', icon: EventOutlinedIcon, title: 'Events',
      features: [
        { key: 'browse-events', name: 'Browse career fairs and info sessions', free: true, premium: true },
        { key: 'post-event', name: 'Post an event', free: true, premium: true },
        { key: 'save-event', name: 'Save events you plan to attend', free: true, premium: true },
      ],
    },
    {
      key: 'upskill', tone: 'primary', icon: SchoolOutlinedIcon, title: 'Upskill',
      features: off('courses') ? [] : [
        { key: 'course-suggestions', name: 'Course and certification suggestions from your resume and goals', free: true, premium: true },
        { key: 'browse-courses', name: 'Browse courses, videos and certifications', free: true, premium: true },
        { key: 'share-course', name: 'Add a course link to share with other candidates', free: true, premium: true },
      ],
    },
    {
      key: 'tasks', tone: 'warning', icon: ChecklistRtlIcon, title: 'Tasks',
      features: [
        { key: 'personal-tasks', name: 'Personal tasks', free: true, premium: true },
        { key: 'group-tasks', name: 'Group tasks', free: true, premium: true },
        { key: 'notes', name: 'Notes, with sharing', free: true, premium: true },
        { key: 'calendar', name: 'Calendar (Google or Microsoft)', free: true, premium: true },
      ],
    },
    {
      key: 'interactions', tone: 'secondary', icon: ForumOutlinedIcon, title: 'Interactions',
      features: [
        { key: 'web', name: 'Web, in a browser or on mobile', free: true, premium: true },
        { key: 'chat', name: `Chat with ${brand.name}`, free: true, premium: true },
        { key: 'talk', name: `Talk to ${brand.name} by voice`, free: false, premium: true },
      ],
    },
  ];

  return groups.filter((group) => group.features.length > 0);
};

/** A tick or a cross, spoken as words as well, with a limit beside it where there is one. */
const Mark = ({ plan, value }) => {
  const on = value !== false;
  const text = on && typeof value === 'object' ? value.note : null;
  return (
    <Box
      data-mark={on ? 'yes' : 'no'}
      sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.25, sm: 1 } }}
    >
      {on
        ? <CheckCircleIcon aria-hidden fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
        : <CancelIcon aria-hidden fontSize="small" sx={{ color: 'error.main', flexShrink: 0 }} />}
      <Box component="span" sx={visuallyHidden}>{on ? `Included in ${plan}` : `Not included in ${plan}`}</Box>
      {text && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>{text}</Typography>}
    </Box>
  );
};

/**
 * The comparison. `limits` is `pricing.limits` from the server, or nothing, in which case the figures are simply absent.
 */
const PlanComparison = ({ limits }) => {
  const groups = comparisonGroups(limits);
  return (
    <Box data-plan-comparison sx={{ mt: 3 }}>
      <Typography variant="h6" component="h4" id="plan-comparison-heading" sx={{ fontWeight: 700, mb: 1.5 }}>
        Free and Premium compared
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-labelledby="plan-comparison-heading" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: { xs: '40%', sm: '44%' }, fontWeight: 700 }}>Feature</TableCell>
              <TableCell sx={{ width: '28%', fontWeight: 700 }}>Free plan</TableCell>
              <TableCell sx={{ width: { xs: '32%', sm: '28%' }, fontWeight: 700, color: 'primary.main' }}>Premium plan</TableCell>
            </TableRow>
          </TableHead>
          {groups.map((group) => {
            const Icon = group.icon;
            return (
              <TableBody key={group.key} data-group={group.key}>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    component="th"
                    scope="colgroup"
                    sx={{
                      py: 1, fontWeight: 700,
                      bgcolor: (theme) => alpha(toneOf(theme, group.tone), 0.12),
                      borderLeft: (theme) => `5px solid ${toneOf(theme, group.tone)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon aria-hidden fontSize="small" sx={{ color: (theme) => toneOf(theme, group.tone) }} />
                      {group.title}
                    </Box>
                  </TableCell>
                </TableRow>
                {group.features.map((feature) => (
                  <TableRow key={feature.key} data-feature={feature.key}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 400, py: 0.75 }}>{feature.name}</TableCell>
                    <TableCell sx={{ py: 0.75 }}><Mark plan="Free" value={feature.free} /></TableCell>
                    <TableCell sx={{ py: 0.75 }}><Mark plan="Premium" value={feature.premium} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            );
          })}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PlanComparison;
